import { addMinutes } from "date-fns";

import createId from "@/lib/create-id";

import { getMessageTemplateOffsetMinutes } from "./get-message-template-offset";
import {
  type CarePlanBranch,
  CarePlanStepStatus,
  type PatientCarePlanStep,
  StepType,
  toSequenceDrafts,
} from "./types";

/**
 * Minimal shape needed from template steps — accepts both TemplateStep and
 * CarePlanTemplateStepDetail so the function works at both the component
 * layer and the API response layer.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TemplateStepInput = any;

export function instantiateTemplate(
  templateSteps: TemplateStepInput[],
  startDate: Date,
): PatientCarePlanStep[] {
  const idMap = new Map<string, string>(); // old template ID -> new ID
  const dateMap = new Map<string, Date>(); // new ID -> scheduled date
  const result: PatientCarePlanStep[] = [];
  let lastDate = startDate;

  // Separate non-message steps from message steps so messages can
  // resolve their parent IDs after all parents are created.
  const messageSteps: TemplateStepInput[] = [];
  const nonMessageSteps: TemplateStepInput[] = [];

  for (const step of templateSteps) {
    if (step.type === StepType.MESSAGE && step.parentStepId) {
      messageSteps.push(step);
    } else {
      nonMessageSteps.push(step);
    }
  }

  // Phase 1: Create non-message steps
  for (const step of nonMessageSteps) {
    const newId = createId("cps");

    if (step.id) {
      idMap.set(step.id, newId);
    }

    const scheduledDate = step.minutesAfterPrevious
      ? addMinutes(lastDate, step.minutesAfterPrevious)
      : lastDate;
    lastDate = scheduledDate;
    dateMap.set(newId, scheduledDate);

    const instantiated: PatientCarePlanStep = {
      ...step,
      kind: "patient",
      id: newId,
      templateStepId: step.id,
      parentStepId: step.parentStepId
        ? (idMap.get(step.parentStepId) ?? step.parentStepId)
        : undefined,
      scheduledDate: scheduledDate.toISOString(),
      status: CarePlanStepStatus.PENDING,
      sequences: Array.isArray(step.sequences)
        ? toSequenceDrafts(step.sequences)
        : undefined,
    };

    // Instantiate branch steps with computed dates
    if (step.type === StepType.DECISION && step.branches?.length) {
      instantiated.branches = step.branches.map(
        (branch: { id: string; steps: TemplateStepInput[] }) =>
          instantiateBranch(branch, scheduledDate, idMap),
      );
    }

    result.push(instantiated);
  }

  // Phase 2: Create message steps with resolved parent IDs
  for (const step of messageSteps) {
    const newId = createId("cps");
    const newParentId = step.parentStepId
      ? (idMap.get(step.parentStepId) ?? step.parentStepId)
      : undefined;
    const parentDate = newParentId
      ? (dateMap.get(newParentId) ?? lastDate)
      : lastDate;
    const offset = getMessageTemplateOffsetMinutes(step);
    const scheduledDate = addMinutes(parentDate, offset);
    dateMap.set(newId, scheduledDate);

    result.push({
      ...step,
      kind: "patient",
      id: newId,
      templateStepId: step.id,
      parentStepId: newParentId,
      scheduledDate: scheduledDate.toISOString(),
      status: CarePlanStepStatus.PENDING,
      sequences: Array.isArray(step.sequences)
        ? toSequenceDrafts(step.sequences)
        : undefined,
    });
  }

  return result;
}

function instantiateBranch(
  branch: { id: string; steps: TemplateStepInput[]; [key: string]: unknown },
  decisionDate: Date,
  parentIdMap: Map<string, string>,
): CarePlanBranch {
  const newBranchId = createId("cpb");
  const branchIdMap = new Map<string, string>(parentIdMap);
  const dateMap = new Map<string, Date>();
  const steps: PatientCarePlanStep[] = [];
  let prevDate = decisionDate;

  // Two-phase handling mirrors the top-level instantiateTemplate:
  // non-message steps first so message parentStepId mappings resolve correctly.
  const messageSteps: TemplateStepInput[] = [];
  const nonMessageSteps: TemplateStepInput[] = [];

  for (const step of branch.steps as TemplateStepInput[]) {
    if (step.type === StepType.MESSAGE && step.parentStepId) {
      messageSteps.push(step);
    } else {
      nonMessageSteps.push(step);
    }
  }

  // Phase 1: non-message steps (including nested DECISION steps)
  for (const step of nonMessageSteps) {
    const newId = createId("cps");
    if (step.id) branchIdMap.set(step.id, newId);

    const scheduledDate = step.minutesAfterPrevious
      ? addMinutes(prevDate, step.minutesAfterPrevious)
      : prevDate;
    prevDate = scheduledDate;
    dateMap.set(newId, scheduledDate);

    const instantiated: PatientCarePlanStep = {
      ...step,
      kind: "patient",
      id: newId,
      templateStepId: step.id,
      branchId: newBranchId,
      parentStepId: step.parentStepId
        ? (branchIdMap.get(step.parentStepId) ?? step.parentStepId)
        : undefined,
      scheduledDate: scheduledDate.toISOString(),
      status: CarePlanStepStatus.PENDING,
      sequences: Array.isArray(step.sequences)
        ? toSequenceDrafts(step.sequences)
        : undefined,
    };

    // Recurse into nested decision branches
    if (step.type === StepType.DECISION && step.branches?.length) {
      instantiated.branches = step.branches.map(
        (nested: { id: string; steps: TemplateStepInput[] }) =>
          instantiateBranch(nested, scheduledDate, branchIdMap),
      );
    }

    steps.push(instantiated);
  }

  // Phase 2: message steps with resolved parent IDs
  for (const step of messageSteps) {
    const newId = createId("cps");
    const newParentId = step.parentStepId
      ? (branchIdMap.get(step.parentStepId) ?? step.parentStepId)
      : undefined;
    const parentDate = newParentId
      ? (dateMap.get(newParentId) ?? prevDate)
      : prevDate;
    const offset = getMessageTemplateOffsetMinutes(step);
    const scheduledDate = addMinutes(parentDate, offset);
    dateMap.set(newId, scheduledDate);

    steps.push({
      ...step,
      kind: "patient",
      id: newId,
      templateStepId: step.id,
      branchId: newBranchId,
      parentStepId: newParentId,
      scheduledDate: scheduledDate.toISOString(),
      status: CarePlanStepStatus.PENDING,
      sequences: Array.isArray(step.sequences)
        ? toSequenceDrafts(step.sequences)
        : undefined,
    });
  }

  return {
    ...branch,
    id: newBranchId,
    templateBranchId: branch.id,
    decisionStepId:
      (branch as { decisionStepId?: string }).decisionStepId ?? "",
    position: (branch as { position?: number }).position ?? 0,
    tagIds: (branch as { tagIds?: string[] }).tagIds ?? [],
    steps,
  };
}
